import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ArrowLeft, ChartBar, ChartLine, ChartPie, Storefront, Basket } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate, monthShort } from "@/lib/format";
import { Card, Loading, Pill, SectionTitle, catColor } from "@/components/riq";

const PERIODS = [
  { k: "7d", l: "7 days" }, { k: "30d", l: "30 days" }, { k: "this_month", l: "This month" },
  { k: "last_month", l: "Last month" }, { k: "3m", l: "3 months" }, { k: "6m", l: "6 months" }, { k: "12m", l: "12 months" },
];

const ITEM_SORTS = [
  { k: "frequent", l: "Most frequent" },
  { k: "quantity", l: "Highest quantity" },
  { k: "spend", l: "Highest spend" },
];

function itemMetric(it, mode, cur) {
  if (mode === "frequent") return `${it.purchases}×`;
  if (mode === "quantity") return `${(it.total_quantity ?? 0).toLocaleString()} units`;
  return money(it.total_spend, cur);
}

export default function Analytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const categoryParam = params.get("category");
  const cur = user?.currency || "ZAR";
  const [period, setPeriod] = useState("this_month");
  const [chartType, setChartType] = useState("bar");
  const [itemSort, setItemSort] = useState("spend");
  const [data, setData] = useState(null);
  const [catDetail, setCatDetail] = useState(null);

  useEffect(() => {
    if (categoryParam) {
      setCatDetail(null);
      api.get(`/analytics/category/${encodeURIComponent(categoryParam)}?period=${period}&include_subs=true`)
        .then((r) => setCatDetail(r.data))
        .catch(() => setCatDetail({ total: 0, transactions: 0, average_transaction: 0, merchants: [], items: [] }));
      return;
    }
    setData(null);
    // Fetch each widget independently — one failing call must not blank the whole page.
    const safe = (p, fallback) => p.then((r) => r.data).catch(() => fallback);
    Promise.all([
      safe(api.get(`/analytics/summary?period=${period}&include_subs=true`), { total_spend: 0, receipt_count: 0, average_transaction: 0, change_pct: null }),
      safe(api.get(`/analytics/categories?period=${period}&include_subs=true`), []),
      safe(api.get(`/analytics/merchants?period=${period}`), []),
      safe(api.get(`/analytics/timeseries?period=${period}&include_subs=true`), []),
      safe(api.get(`/analytics/items`), []),
      safe(api.get(`/budgets?period=${period}&include_subs=true`), { monthly_budget: null, categories: [] }),
    ]).then(([s, c, m, t, i, b]) => setData({ summary: s, categories: c, merchants: m, timeseries: t, items: i, budget: b }));
  }, [period, categoryParam]);

  // Category detail view
  if (categoryParam) {
    if (!catDetail) return <Loading />;
    return (
      <div className="space-y-6">
        <button onClick={() => navigate("/analytics")} className="flex items-center gap-2 text-neutral-500 font-medium"><ArrowLeft size={18} /> Analytics</button>
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full" style={{ background: catColor(categoryParam) }} />
          <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">{categoryParam}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">{PERIODS.map((p) => <Pill key={p.k} active={period === p.k} onClick={() => setPeriod(p.k)} testId={`period-${p.k}`}>{p.l}</Pill>)}</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total</div><div className="font-head text-2xl font-bold text-riq-primary mt-1">{money(catDetail.total, cur)}</div></Card>
          <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Transactions</div><div className="font-head text-2xl font-bold text-riq-primary mt-1">{catDetail.transactions}</div></Card>
          <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Avg. transaction</div><div className="font-head text-2xl font-bold text-riq-primary mt-1">{money(catDetail.average_transaction, cur)}</div></Card>
        </div>
        {catDetail.merchants.length > 0 && <Card><h3 className="font-head font-semibold text-riq-primary mb-4">Merchants</h3><div className="space-y-2">{catDetail.merchants.map((m) => <div key={m.merchant} className="flex justify-between text-sm"><span>{m.merchant}</span><span className="font-semibold">{money(m.total, cur)}</span></div>)}</div></Card>}
        {catDetail.items.length > 0 && <Card><h3 className="font-head font-semibold text-riq-primary mb-4">Top items</h3><div className="space-y-2">{catDetail.items.map((it) => <div key={it.canonical_name} className="flex justify-between text-sm"><span>{it.canonical_name} <span className="text-muted-foreground">×{it.purchases}</span></span><span className="font-semibold">{money(it.total_spend, cur)}</span></div>)}</div></Card>}
      </div>
    );
  }

  if (!data) return <Loading />;
  const { summary, categories, merchants, timeseries, items, budget } = data;
  const sortedItems = [...(items || [])].sort((a, b) => {
    if (itemSort === "frequent") return (b.purchases || 0) - (a.purchases || 0);
    if (itemSort === "quantity") return (b.total_quantity || 0) - (a.total_quantity || 0);
    return (b.total_spend || 0) - (a.total_spend || 0);
  }).slice(0, 6);

  return (
    <div className="space-y-6">
      <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">Analytics</h1>

      <div className="flex gap-2 flex-wrap">{PERIODS.map((p) => <Pill key={p.k} active={period === p.k} onClick={() => setPeriod(p.k)} testId={`period-${p.k}`}>{p.l}</Pill>)}</div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniCard label="Total spend" value={money(summary.total_spend, cur)} />
        <MiniCard label="Receipts" value={summary.receipt_count} />
        <MiniCard label="Avg. transaction" value={money(summary.average_transaction, cur)} />
        <MiniCard label="vs previous" value={summary.change_pct != null ? `${summary.change_pct > 0 ? "+" : ""}${summary.change_pct}%` : "—"} accent={summary.change_pct > 0} />
      </div>

      {/* Spend chart with type toggle */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-semibold text-riq-primary">Spend over time</h3>
          <div className="flex gap-1 bg-secondary rounded-full p-1">
            <TabBtn active={chartType === "bar"} onClick={() => setChartType("bar")} icon={ChartBar} testId="chart-bar" />
            <TabBtn active={chartType === "line"} onClick={() => setChartType("line")} icon={ChartLine} testId="chart-line" />
            <TabBtn active={chartType === "category"} onClick={() => setChartType("category")} icon={ChartPie} testId="chart-category" />
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          {chartType === "bar" ? (
            <BarChart data={timeseries} margin={{ left: -10, right: 10 }}>
              <XAxis dataKey="label" tickFormatter={shortDate} tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} minTickGap={20} />
              <YAxis tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} width={45} />
              <Tooltip formatter={(v) => money(v, cur)} labelFormatter={shortDate} contentStyle={{ borderRadius: 12, border: "1px solid #E9EAF2", fontSize: 13 }} />
              <Bar dataKey="value" fill="#6E3FFC" radius={[6, 6, 0, 0]} />
            </BarChart>
          ) : chartType === "line" ? (
            <LineChart data={timeseries} margin={{ left: -10, right: 10 }}>
              <XAxis dataKey="label" tickFormatter={shortDate} tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} minTickGap={20} />
              <YAxis tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} width={45} />
              <Tooltip formatter={(v) => money(v, cur)} labelFormatter={shortDate} contentStyle={{ borderRadius: 12, border: "1px solid #E9EAF2", fontSize: 13 }} />
              <Line type="monotone" dataKey="value" stroke="#6E3FFC" strokeWidth={2.5} dot={false} />
            </LineChart>
          ) : (
            <PieChart>
              <Pie data={categories} dataKey="total" nameKey="category" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                {categories.map((c) => <Cell key={c.category} fill={catColor(c.category)} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v, cur)} contentStyle={{ borderRadius: 12, border: "1px solid #E9EAF2", fontSize: 13 }} />
            </PieChart>
          )}
        </ResponsiveContainer>
      </Card>

      {/* Categories */}
      <div>
        <SectionTitle>Categories</SectionTitle>
        <Card className="divide-y divide-riq-border p-0">
          {categories.map((c) => (
            <button key={c.category} onClick={() => navigate(`/analytics?category=${encodeURIComponent(c.category)}`)} className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/40 text-left">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: catColor(c.category) }} />
              <span className="flex-1 text-sm font-medium">{c.category}</span>
              <span className="text-xs text-muted-foreground">{c.transactions} txns</span>
              <span className="font-head font-semibold w-24 text-right">{money(c.total, cur)}</span>
            </button>
          ))}
        </Card>
      </div>

      {/* Merchants + Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center gap-2 mb-4"><Storefront size={18} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Top merchants</h3></div>
          <div className="space-y-2.5">
            {merchants.slice(0, 6).map((m) => (
              <div key={m.merchant} className="flex items-center justify-between text-sm"><span>{m.merchant} <span className="text-muted-foreground text-xs">· {m.visits} visits</span></span><span className="font-semibold">{money(m.total, cur)}</span></div>
            ))}
            {merchants.length === 0 && <div className="text-sm text-muted-foreground">No data for this period.</div>}
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-2 mb-3"><Basket size={18} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Top items</h3></div>
          <div className="flex gap-1.5 flex-wrap mb-4">
            {ITEM_SORTS.map((s) => (
              <button
                key={s.k}
                data-testid={`item-sort-${s.k}`}
                onClick={() => setItemSort(s.k)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${itemSort === s.k ? "bg-riq-primary text-white" : "bg-secondary text-neutral-500 hover:bg-secondary/70"}`}
              >
                {s.l}
              </button>
            ))}
          </div>
          <div className="space-y-2.5" data-testid="analytics-items-list">
            {sortedItems.map((it) => (
              <button key={it.canonical_name} data-testid={`analytics-item-${it.canonical_name}`} onClick={() => navigate(`/items/${encodeURIComponent(it.canonical_name)}`)} className="w-full flex items-center justify-between text-sm text-left hover:text-riq-primary">
                <span className="truncate pr-2">
                  {it.canonical_name}
                  {it.last_purchase && <span className="block text-xs text-muted-foreground">{monthShort(it.last_purchase)}</span>}
                </span>
                <span className="font-semibold shrink-0">{itemMetric(it, itemSort, cur)}</span>
              </button>
            ))}
            {sortedItems.length === 0 && <div className="text-sm text-muted-foreground">No items for this period.</div>}
          </div>
        </Card>
      </div>

      {/* Budget utilisation */}
      {budget.monthly_budget && (
        <Card>
          <h3 className="font-head font-semibold text-riq-primary mb-4">Budget utilisation</h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1.5"><span className="text-muted-foreground">Monthly · forecast month-end {money(budget.forecast_month_end, cur)}</span><span className="font-semibold">{money(budget.spent, cur)} / {money(budget.monthly_budget, cur)}</span></div>
            <div className="h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(budget.pct_used, 100)}%`, background: budget.pct_used > 90 ? "#FF6B6B" : "#6E3FFC" }} /></div>
          </div>
          {budget.categories.map((c) => (
            <div key={c.category} className="mb-3">
              <div className="flex justify-between text-sm mb-1.5"><span>{c.category}</span><span className="text-muted-foreground">{c.pct_used}% · {money(c.spent, cur)}/{money(c.budget, cur)}</span></div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(c.pct_used, 100)}%`, background: c.pct_used > 90 ? "#FF6B6B" : catColor(c.category) }} /></div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function MiniCard({ label, value, accent }) {
  return <Card className="p-4"><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div><div className="font-head text-xl font-bold mt-1" style={{ color: accent ? "#FF6B6B" : "#6E3FFC" }}>{value}</div></Card>;
}
function TabBtn({ active, onClick, icon: Icon, testId }) {
  return <button data-testid={testId} onClick={onClick} className={`w-9 h-8 rounded-full flex items-center justify-center transition-colors ${active ? "bg-white shadow-sm text-riq-primary" : "text-neutral-400"}`}><Icon size={18} weight={active ? "fill" : "regular"} /></button>;
}
